/**
 * B68(b) — confirmation-page vocabulary, pure. Kernel §5.2 entitlement
 * states never render to buyers (B31g's rule): they map to buyer words
 * here or they do not render at all. The order summary joins the cart
 * mirror against the catalog cards (variant DISPLAY names) and the slot
 * labels the session captured when the buyer picked a departure — facts
 * the app already holds; the read surface stays as lean as B25 left it.
 */
import { clockWords, slotFullWords, slotWords, type WhenOptions } from "./when.js";
import type { CartLine, ProductSummary } from "../ui/screens.js";

/** B448 — exported so the mobile wallets' parity fixture byte-compares the WHOLE table (a state the web grows cannot be left behind). */
export const BUYER_STATUS: Readonly<Record<string, string>> = {
  ISSUED: "Valid",
  ACTIVE: "Valid",
  EXHAUSTED: "Used",
  EXPIRED: "Expired",
  VOIDED: "Canceled",
};

/** Buyer word for a §5.2 state; unknown states render NOTHING, never the raw string. */
export function buyerTicketStatus(state: string): string | undefined {
  return BUYER_STATUS[state];
}

/**
 * One buyer-readable line for a departure, in the display zone.
 * B403 wave-2 residual — the register moved to lib/when.ts: the in-flow
 * face is the frames' "Fri 15 · 10:30" (73:2's cart rows), never the
 * locale dump "Wed, Aug 26, 12:30 PM" the measure found.
 */
export function slotDisplay(startMs: number, opts: { locale?: string; timeZone?: string } = {}): string {
  return slotWords(startMs, opts);
}

/** A clock time in buyer words — the frames' 24h register ("14:30"), no seconds (B142). */
export function timeDisplay(ms: number, opts: { locale?: string; timeZone?: string } = {}): string {
  return clockWords(ms, opts);
}

/**
 * B142 — the composer's departure line: the day-words display, with the
 * arrival as a plain clock time when the final leg's sibling is known.
 * Display only; an unknown arrival stays unwritten.
 */
export function departureLabel(startMs: number, arrivalMs?: number, opts: { locale?: string; timeZone?: string } = {}): string {
  const base = slotDisplay(startMs, opts);
  return arrivalMs === undefined ? base : `${base} → arrives ${timeDisplay(arrivalMs, opts)}`;
}

export interface SummaryLine {
  readonly lineId: string;
  readonly text: string;
}

/** The order summary: quantity × product (variant display name) — slot label. */
export function summaryLines(
  lines: readonly CartLine[],
  products: readonly ProductSummary[],
  slotLabels: Readonly<Record<string, string>>,
): readonly SummaryLine[] {
  return lines.map((l) => {
    const product = products.find((p) => p.productId === l.productId);
    /* B238(b) — B239's `|| variantId` arm, third sighting and the first on
       a page the BUYER keeps: a variant the catalog no longer names used to
       degrade to the wire's own id. Unknown stays unwritten, so the line
       names the product alone rather than printing an id (R7/B145) — and
       the parenthetical goes with it, leaving no empty slot behind. */
    const variantName =
      l.variantId === undefined
        ? undefined
        : product?.variants.find((v) => v.variantId === l.variantId)?.name;
    const slot = l.slotId === undefined ? undefined : slotLabels[l.slotId];
    const name = product?.name ?? l.productName;
    const parts = [
      `${l.quantity} × ${name}`,
      variantName !== undefined ? ` (${variantName})` : "",
      slot !== undefined ? ` — ${slot}` : "",
    ];
    return { lineId: l.lineId, text: parts.join("") };
  });
}

/* ---------------------------------------------------- B397 leg (4), 74:2 */

/** Irregular buyer nouns the party sentence must not mangle. */
const IRREGULAR_PLURALS: Readonly<Record<string, string>> = {
  child: "children",
  person: "people",
};

/**
 * 74:2's party segment: "2 adults, 1 child". The noun is the variant's
 * DISPLAY name with its parenthetical shed ("Child (2-12)" → child); a
 * variant the catalog no longer names drops from the sentence rather than
 * echoing an id (B238(b)'s arm, fourth sighting priced in advance).
 */
export function partyFromLines(lines: readonly CartLine[], products: readonly ProductSummary[]): string | undefined {
  const parts: string[] = [];
  for (const l of lines) {
    const product = products.find((p) => p.productId === l.productId);
    const name = l.variantId === undefined ? undefined : product?.variants.find((v) => v.variantId === l.variantId)?.name;
    if (name === undefined) continue;
    const noun = name.replace(/\s*\(.*\)\s*$/u, "").toLowerCase();
    const plural = IRREGULAR_PLURALS[noun] ?? (/s$/u.test(noun) ? noun : `${noun}s`);
    parts.push(`${l.quantity} ${l.quantity === 1 ? noun : plural}`);
  }
  return parts.length === 0 ? undefined : parts.join(", ");
}

/**
 * The Done card's one sentence (74:2): product · day-and-time · party.
 * Every segment is a fact the session holds; a missing fact drops its
 * segment whole — never an empty slot, never a placeholder (§10).
 */
export function confirmationSummary(
  lines: readonly CartLine[],
  products: readonly ProductSummary[],
  slotLabels: Readonly<Record<string, string>>,
  opts: WhenOptions = {},
): string | undefined {
  const names = [...new Set(lines.map((l) => products.find((p) => p.productId === l.productId)?.name ?? l.productName))];
  /* B403 wave-2 residual — 74:2's summary speaks the FULLER day
     ("Fri 15 Aug · 10:30"): derive it from the line's own start where the
     cart carried one, and keep the captured label as the honest fallback. */
  const slots = [
    ...new Set(
      lines
        .map((l) =>
          l.slotStart !== undefined
            ? slotFullWords(l.slotStart, opts)
            : l.slotId === undefined
              ? undefined
              : slotLabels[l.slotId],
        )
        .filter((s): s is string => s !== undefined),
    ),
  ];
  const party = partyFromLines(lines, products);
  const parts = [...names, ...slots, ...(party === undefined ? [] : [party])];
  return parts.length === 0 ? undefined : parts.join(" · ");
}

const COUNT_WORDS: readonly string[] = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];

/**
 * 74:2's gate sentence, every clause gated on a held fact (§10): the
 * covers-all clause states the ONE human code (true — B115 mints one per
 * order); the email clause renders only when checkout carried a buyer
 * email, and says on-its-way because no read carries a sent receipt —
 * the frame's "in your email" would claim a fact we cannot see.
 */
export function gateSentence(ticketCount: number, emailKnown: boolean): string | undefined {
  if (ticketCount === 0) return undefined;
  /* B403 (74:2, the copy wall): the frame's own words — "Show this" (the
     CODE is the shown thing) and "one code", not the wire's noun. The
     email clause keeps the recorded §10 deviation: "on its way", because
     no read carries a sent receipt and "in your email" would claim a
     fact we cannot see. */
  const shown =
    ticketCount === 1
      ? "Show this at the gate."
      : `Show this at the gate — one code covers all ${COUNT_WORDS[ticketCount] ?? String(ticketCount)} tickets.`;
  return emailKnown ? `${shown} It is also on its way to your email.` : shown;
}
